import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Button, Grid, Stack, Tooltip, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { useNavigate } from 'react-router-dom';
import { endpoint } from '../../pages';
import './ButtonPanel.css';

import ResultsPdfDocument from '../PDF/v2/container_yard/ResultsPdfV2';
import ResultsPdfDocumentUnpaved from '../PDF/v2/unpaved/ResultsPdfV2';
import ResultsPdfDocumentPaved from '../PDF/v2/paved/ResultsPdfV2';
import AirportResultsPdfDocument from '../PDF/v2/airport/ResultsPdfV2';
import RailwayResultsPdfDocument from '../PDF/v2/railway/ResultsPdfV2';
import GeoGridResultsPdfDocument from '../PDF/v2/container_yard/GeoGridResultsPdfV2';
import PavedGeoGridResultsPdfDocument from '../PDF/v2/paved/GeoGridResultsPdfV2';
import UnpavedGeoGridResultsPdfDocument from '../PDF/v2/unpaved/GeoGridResultsPdfV2';
import RailwayGeoGridResultsPdfDocument from '../PDF/v2/railway/GeoGridResultsPdfV2';
import AirportGeoGridResultsPdfDocument from '../PDF/v2/airport/GeoGridResultsPdfV2';
import PlaceholderPdfDocument from '../PDF/PlaceholderPdfDocument';
import CombinedResultsPdfDocument from '../PDF/v2/container_yard/CombinedResultsPdfV2';
import UnpavedCombinedResultsPdfDocument from '../PDF/v2/unpaved/CombinedResultsPdfV2';
import PavedCombinedResultsPdfDocument from '../PDF/v2/paved/CombinedResultsPdfV2';
import RailwayCombinedResultsPdfDocument from '../PDF/v2/railway/CombinedResultsPdfV2';
import AirportCombinedResultsPdfDocument from '../PDF/v2/airport/CombinedResultsPdfV2';
import { IGeogridData, IDynamicProjectOutputData } from '../../api/IApiResponse';

import SaveIcon from '@mui/icons-material/Save';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import GridOnIcon from '@mui/icons-material/GridOn';
import { EProjectType } from '../../enum/project';
import { logDebug, logWarn, logError, LogTag } from '../../utils/utils';
import { pdfImageCaptureService } from '../../services/PdfImageCaptureService';

// Import refactored utilities and hooks
import { IButtonPanelProps, IPdfProjectInfo } from './types';
import { useButtonPanelState, useGeotextileSelection } from './hooks/useButtonPanelState';
import { imageExtractors } from './utils/imageExtractors';
import { pdfValidators } from './utils/pdfValidators';
import { getEffectiveImageProps, getProjectImageData, getIsGeneratingImages, getPdfError, getGeoGridProps, getCombinedPdfProps } from './utils/propMappers';

// Staggered PDF component wrapper to prevent concurrent Yoga initialization
interface StaggeredPDFDownloadLinkProps {
    document: React.ReactElement;
    fileName: string;
    children: ((props: { loading: boolean; error: Error | null; url?: string | null; blob?: Blob | null }) => React.ReactNode) | React.ReactNode;
    pdfKey: number;
    style?: React.CSSProperties;
    delay?: number;
}

const StaggeredPDFDownloadLink: React.FC<StaggeredPDFDownloadLinkProps> = ({ document, fileName, children, pdfKey, style, delay = 0 }) => {
    const [isReady, setIsReady] = useState(delay === 0);

    useEffect(() => {
        if (delay > 0) {
            const timer = setTimeout(() => setIsReady(true), delay);
            return () => clearTimeout(timer);
        }
    }, [delay]);

    if (!isReady) {
        return <span style={style}>{typeof children === 'function' ? children({ loading: true, error: null }) : children}</span>;
    }

    return (
        <PDFDownloadLink document={document} fileName={fileName} key={pdfKey} style={style}>
            {children as any}
        </PDFDownloadLink>
    );
};

export const ButtonPanel: React.FC<IButtonPanelProps> = props => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    // Extract common props
    const {
        showSave = false,
        showExportPdf = false,
        showExportGeoGridPdf = false,
        showExportCombinedPdf = false,
        showGeoGrid = false,
        showExecute = false,
        showExecuteGeoGrid = false,
        onSave,
        onExecute,
        onExportToPDF,
        saveDisabled = false,
        showGeoGridDisabled = false,
        executeDisabled = false,
        executeGeoGridDisabled = false,
        useUnifiedProps = false,
        isReadOnly = false,
    } = props;

    // Use refactored custom hooks
    const buttonPanelState = useButtonPanelState();
    const { projectInfo, resultsData, aashtoResults, projectType, projectGuid, dynamicProject } = buttonPanelState;
    const hasGeotextileSelected = useGeotextileSelection(projectInfo);

    // UNPAVED_LENG_GEOTEXTILE: Debug geotextile detection in ButtonPanel
    logDebug(
        'ButtonPanel',
        'UNPAVED_LENG_GEOTEXTILE: Geotextile detection debug',
        {
            hasGeotextileSelected,
            projectType: projectType,
            projectInfoExists: !!projectInfo,
            projectInfoData: projectInfo?.data,
            projectInfoDataMethod: projectInfo?.data?.method,
            dynamicProjectMethod: dynamicProject?.data?.method, // Top-level method (source of truth)
            // FLAT structure: geoTextile is directly on data
            geoTextile: (projectInfo?.data as any)?.geoTextile,
            selectedGeotextile: (projectInfo?.data as any)?.geoTextile?.selectedGeotextile,
        },
        LogTag.UNPAVED_LENG_GEOTEXTILE
    );

    // Use extracted utilities
    const effectiveProps = getEffectiveImageProps(props);

    // Remove local GeoGrid states since they're now passed as props
    const [pdfKey, setPdfKey] = useState(Date.now());

    const projectTypeEnum = effectiveProps.projectType || projectType;

    useEffect(() => {
        setPdfKey(Date.now());
    }, [i18n.language]);

    const handleSave = () => {
        if (onSave) {
            onSave();
        } else {
            logWarn('ButtonPanel', 'onSave prop not provided.', undefined, LogTag.UI_INTERACTIONS);
        }
    };

    const handleExecute = () => {
        logDebug('ButtonPanel', 'Execute button clicked', undefined, LogTag.UI_INTERACTIONS);
        if (onExecute) {
            onExecute();
        } else {
            logWarn('ButtonPanel', 'onExecute prop not provided.', undefined, LogTag.UI_INTERACTIONS);
            logDebug('ButtonPanel', 'Execute button clicked (default action)', undefined, LogTag.UI_INTERACTIONS);
        }
    };

    const handleShowGeoGrid = () => {
        logDebug('ButtonPanel', 'Show GeoGrid button clicked', undefined, LogTag.UI_INTERACTIONS);
        if (dynamicProject?.data?.guid) {
            // Set read-only mode flag before navigation
            try {
                sessionStorage.setItem(`geogridViewMode_${dynamicProject.data.guid}`, 'readonly');
                logDebug('ButtonPanel', `Set readonly mode for project ${dynamicProject.data.guid}`, undefined, LogTag.NAVIGATION);
            } catch (error) {
                logWarn('ButtonPanel', 'SessionStorage unavailable, proceeding without readonly protection', error, LogTag.NAVIGATION);
            }

            const targetEndpoint = endpoint.geogrid.replace(':id', dynamicProject.data.guid);
            logDebug('ButtonPanel', `Navigating to GeoGrid: ${targetEndpoint}`, undefined, LogTag.NAVIGATION);
            navigate(targetEndpoint);
        } else {
            logWarn('ButtonPanel', 'Cannot navigate to GeoGrid: Project GUID is missing.', undefined, LogTag.NAVIGATION);
        }
    };

    const handleExecuteGeoGrid = () => {
        logDebug('ButtonPanel', 'Execute GeoGrid button clicked', undefined, LogTag.UI_INTERACTIONS);
    };
    logDebug('ButtonPanel', 'projectInfo.data:', projectInfo?.data, LogTag.PROJECT_INFO);
    logDebug('ButtonPanel', 'dynamicProject.data:', dynamicProject?.data, LogTag.PROJECT_INFO);
    const canGenerateBasePdf = !!projectInfo?.data && !!dynamicProject?.data;
    let isSpecificDataReady = false;
    let documentToRender = <PlaceholderPdfDocument message={t('project_data_not_available') || 'Project data not available'} title={t('project_pdf_placeholder_title') || 'Project PDF'} />;

    // Get current project images, error, and generating state using helper functions
    const safeProjectType = (projectTypeEnum as EProjectType) || EProjectType.container_yard;
    const currentProjectImages = getProjectImageData(safeProjectType, effectiveProps);
    const currentPdfError = getPdfError(safeProjectType, effectiveProps);
    const currentIsGeneratingImages = getIsGeneratingImages(safeProjectType, effectiveProps);

    // Get GeoGrid and Combined PDF props
    const geoGridProps = getGeoGridProps(effectiveProps);
    const combinedPdfHelperProps = getCombinedPdfProps(effectiveProps, geoGridProps);

    if (projectTypeEnum === EProjectType.container_yard || projectTypeEnum === EProjectType.parking_deck) {
        const containerImages = imageExtractors.getContainerYardImages(currentProjectImages);
        isSpecificDataReady = pdfValidators.validateContainerYardImages(containerImages, projectTypeEnum);

        if (isSpecificDataReady && projectInfo?.data && dynamicProject.data && containerImages) {
            documentToRender = (
                <ResultsPdfDocument
                    output={resultsData}
                    projectInfo={projectInfo.data}
                    dynamicProject={dynamicProject}
                    tireImageUnreinforced={containerImages.tireImg1 as string}
                    tireImageReinforced={containerImages.tireImg2 as string}
                    chartImage={containerImages.chartImg as string}
                    t={t}
                />
            );
        }
    } else if (projectTypeEnum === EProjectType.unpaved) {
        // For unpaved, use dedicated function that reads directly from sessionStorage
        const unpavedImages = imageExtractors.getUnpavedImages(projectGuid || undefined);

        // Validate unpaved images based on geotextile selection
        isSpecificDataReady = pdfValidators.validateUnpavedImages(unpavedImages, hasGeotextileSelected);

        // Simple validation logging
        logDebug(
            'ButtonPanel',
            'Unpaved PDF validation:',
            {
                hasGeotextileSelected,
                requiredImages: hasGeotextileSelected ? '3 (with geotextile)' : '2 (without geotextile)',
                imagesReady: `${[!!unpavedImages?.tireImg1, !!unpavedImages?.tireImg2, hasGeotextileSelected ? !!unpavedImages?.tireImg3 : true].filter(Boolean).length}/${hasGeotextileSelected ? 3 : 2}`,
                isReady: isSpecificDataReady,
            },
            LogTag.PDF_EXPORT
        );

        if (isSpecificDataReady && projectInfo?.data && dynamicProject.data && unpavedImages) {
            // UNPAVED_LENG_GEOTEXTILE: Debug PDF rendering props
            logDebug(
                'ButtonPanel',
                'UNPAVED_LENG_GEOTEXTILE: Creating PDF document with props',
                {
                    hasGeotextileSelected,
                    unpavedImages_tireImg1: !!unpavedImages.tireImg1,
                    unpavedImages_tireImg2: !!unpavedImages.tireImg2,
                    unpavedImages_tireImg3: !!unpavedImages.tireImg3,
                    unpavedImages_tireImg3_value: unpavedImages.tireImg3,
                    projectInfo_data: projectInfo.data,
                    passedProjectInfo: projectInfo.data,
                },
                LogTag.UNPAVED_LENG_GEOTEXTILE
            );

            documentToRender = (
                <ResultsPdfDocumentUnpaved
                    output={resultsData}
                    projectInfo={projectInfo.data}
                    dynamicProject={dynamicProject}
                    tireImageUnreinforced={unpavedImages.tireImg1 as string}
                    tireImageReinforced={unpavedImages.tireImg2 as string}
                    tireImageGeotextile={unpavedImages.tireImg3 as string}
                    chartImage={unpavedImages.chartImg as string}
                    t={t}
                />
            );
        }
    } else if (projectTypeEnum === EProjectType.paved) {
        // For paved projects, get images from sessionStorage to include trafficData
        // Use dynamicProject.data.guid if projectGuid is not available
        const guidToUse = projectGuid || dynamicProject.data?.guid || '';
        const storedPavedImages = pdfImageCaptureService.getStoredPavedImages(guidToUse);
        const pavedImages = storedPavedImages || imageExtractors.getPavedImages(currentProjectImages);
        isSpecificDataReady = pdfValidators.validatePavedImages(pavedImages);

        if (isSpecificDataReady && projectInfo?.data && dynamicProject.data && pavedImages) {
            logDebug(
                'ButtonPanel',
                'Rendering Paved PDF with images',
                {
                    hasSurfaceDesign: !!pavedImages?.surfaceDesignImages,
                    hasBaseDesign: !!pavedImages?.baseDesignImages,
                    hasSubbaseDesign: !!pavedImages?.subbaseDesignImages,
                    surfaceCanvasCount: pavedImages?.surfaceDesignImages ? Object.keys(pavedImages.surfaceDesignImages).length : 0,
                    baseCanvasCount: pavedImages?.baseDesignImages ? Object.keys(pavedImages.baseDesignImages).length : 0,
                    subbaseCanvasCount: pavedImages?.subbaseDesignImages ? Object.keys(pavedImages.subbaseDesignImages).length : 0,
                },
                LogTag.DYNAMIC_CANVAS
            );

            // Debug log the exact props being passed to ResultsPdfDocumentPaved
            logDebug(
                'ButtonPanel',
                'Props being passed to ResultsPdfDocumentPaved',
                {
                    surfaceDesignImages: pavedImages?.surfaceDesignImages,
                    baseDesignImages: pavedImages?.baseDesignImages,
                    subbaseDesignImages: pavedImages?.subbaseDesignImages,
                    surfaceDesignImagesType: typeof pavedImages?.surfaceDesignImages,
                    hasCanvas1: !!pavedImages?.surfaceDesignImages?.canvas1,
                    hasCanvas2: !!pavedImages?.surfaceDesignImages?.canvas2,
                    hasCanvas3: !!pavedImages?.surfaceDesignImages?.canvas3,
                    pavedImagesKeys: pavedImages ? Object.keys(pavedImages) : [],
                    surfaceDesignImagesKeys: pavedImages?.surfaceDesignImages ? Object.keys(pavedImages.surfaceDesignImages) : [],
                },
                LogTag.DYNAMIC_CANVAS
            );

            documentToRender = (
                <ResultsPdfDocumentPaved
                    projectInfo={{
                        projectName: dynamicProject.data?.projectName || 'N/A',
                        nrProject: dynamicProject.data?.projectInfo?.data?.nrProject || 'N/A',
                        unit: dynamicProject.data?.unit || 'Metric',
                        projectStatus: 'Active',
                        projectType: dynamicProject.data?.type || 'paved',
                        userId: dynamicProject.data?.idUser || 0,
                        appId: dynamicProject.data?.idApp || 0,
                        lastUpdateDate: new Date(),
                        owner: dynamicProject.data?.projectInfo?.projectOwner || dynamicProject.data?.projectInfo?.owner || 'N/A',
                        designerFirmName: dynamicProject.data?.projectInfo?.designerFirmName || 'N/A',
                        designerFirmCountry: dynamicProject.data?.projectInfo?.designerFirmCountry || 'N/A',
                        designerFirmCity: dynamicProject.data?.projectInfo?.data?.designerFirmCity || 'N/A',
                        coordinates: dynamicProject.data?.projectInfo?.coordinates || ([undefined, undefined] as [number | undefined, number | undefined]),
                        productCountry: dynamicProject.data?.productCountry || null,
                        products: dynamicProject.data?.products || [],
                        notes: dynamicProject.data?.notes || dynamicProject.data?.projectInfo?.data?.notes || '',
                        mapImage: dynamicProject.data?.mapImage || '',
                    }}
                    output={aashtoResults}
                    dynamicProject={dynamicProject}
                    t={t}
                    chartImage={pavedImages.chartImg as string}
                    tireImageUnreinforced={pavedImages.tireImageUnreinforced as string}
                    tireImageReinforced={pavedImages.tireImageReinforced as string}
                    tireImageGeogrid={pavedImages.tireImageGeogrid as string}
                    trafficData={pavedImages?.trafficData}
                    surfaceDesignImages={pavedImages?.surfaceDesignImages}
                    baseDesignImages={pavedImages?.baseDesignImages}
                    subbaseDesignImages={pavedImages?.subbaseDesignImages}
                />
            );
        }
    } else if (projectTypeEnum === EProjectType.airport) {
        // For airport projects, get images from sessionStorage to include dynamic design sections
        const guidToUse = projectGuid || dynamicProject.data?.guid || '';
        const storedAirportImages = pdfImageCaptureService.getStoredAirportImages(guidToUse);
        const airportImages = storedAirportImages || imageExtractors.getAirportImages(currentProjectImages);
        isSpecificDataReady = pdfValidators.validateAirportImages(airportImages);

        if (isSpecificDataReady && airportImages && projectInfo?.data && dynamicProject.data) {
            logDebug(
                'ButtonPanel',
                'Rendering Airport PDF with images',
                {
                    hasSurfaceDesign: !!airportImages?.surfaceDesignImages,
                    hasBaseDesign: !!airportImages?.baseDesignImages,
                    hasSubbaseDesign: !!airportImages?.subbaseDesignImages,
                    surfaceCanvasCount: airportImages?.surfaceDesignImages ? Object.keys(airportImages.surfaceDesignImages).length : 0,
                    baseCanvasCount: airportImages?.baseDesignImages ? Object.keys(airportImages.baseDesignImages).length : 0,
                    subbaseCanvasCount: airportImages?.subbaseDesignImages ? Object.keys(airportImages.subbaseDesignImages).length : 0,
                },
                LogTag.DYNAMIC_CANVAS
            );

            documentToRender = (
                <AirportResultsPdfDocument
                    images={airportImages}
                    projectData={{
                        projectInfo: projectInfo,
                        dynamicProject: dynamicProject,
                        output: resultsData,
                        aashtoResults: dynamicProject.data?.aashtoResults || resultsData,
                    }}
                    surfaceDesignImages={airportImages?.surfaceDesignImages}
                    baseDesignImages={airportImages?.baseDesignImages}
                    subbaseDesignImages={airportImages?.subbaseDesignImages}
                />
            );
        }
    } else if (projectTypeEnum === EProjectType.railways) {
        // For railway projects, use dedicated railway images and component (2 canvas: unreinforced + reinforced)
        const railwayImages = imageExtractors.getRailwayImages(currentProjectImages);

        // Debug logging for railway images

        // Additional debug for currentProjectImages content

        isSpecificDataReady = pdfValidators.validateRailwayImages(railwayImages);

        if (isSpecificDataReady && railwayImages && projectInfo?.data && dynamicProject.data) {
            logDebug('ButtonPanel', 'Railway PDF data ready, rendering document', undefined, LogTag.PDF_EXPORT);
            documentToRender = <RailwayResultsPdfDocument railwayImages={railwayImages} dynamicProject={dynamicProject} output={resultsData} t={t} />;
        }
    }

    // Separate logic for GeoGrid PDF - check if we have geoGrid data regardless of project type
    let isGeoGridDataReady = false;
    let geoGridDocumentToRender = <PlaceholderPdfDocument message={t('geogrid_data_not_available') || 'GeoGrid data not available'} title={t('geogrid_pdf_placeholder_title') || 'GeoGrid PDF'} />;
    let geoGridPdfProps:
        | {
              geoGridData: IGeogridData | undefined;
              geogridImage: string | undefined;
          }
        | Record<string, never> = {};

    // Use previously declared geoGridProps and combinedPdfHelperProps from above

    if (showExportGeoGridPdf) {
        // Prendi geoGridData da Redux
        const geoGridData = dynamicProject.data?.projectInfo?.data?.geoGrid as IGeogridData | undefined;
        logDebug('ButtonPanel', 'GeoGrid Data:', geoGridData, LogTag.GEOGRID_CALC);

        // Basic requirement: geoGrid data present
        isGeoGridDataReady = !!geoGridData && !!geoGridProps.geoGridLayersImage;

        // For airport projects, if we have the GeoGrid layer image but no geoGrid data in the expected location,
        // check if GeoGrid has been run successfully (indicated by geogridHasBeenRan flag)
        if (projectTypeEnum === EProjectType.airport && !geoGridData && !!geoGridProps.geoGridLayersImage) {
            const hasGeoGridResults = !!dynamicProject.data?.geogridHasBeenRan;
            logDebug(
                'ButtonPanel',
                'Airport GeoGrid fallback check:',
                {
                    hasGeoGridLayersImage: !!geoGridProps.geoGridLayersImage,
                    hasGeoGridResults,
                    geogridHasBeenRan: dynamicProject.data?.geogridHasBeenRan,
                },
                LogTag.GEOGRID_CALC
            );

            // Consider GeoGrid data ready if we have the image and geogrid has been run
            isGeoGridDataReady = hasGeoGridResults;
        }

        // For paved projects specifically, log the check but don't require paved images for GeoGrid PDF
        if (projectTypeEnum === EProjectType.paved && geoGridData && dynamicProject.data?.guid) {
            try {
                const storedPavedImages = pdfImageCaptureService.getStoredPavedImages(dynamicProject.data.guid);
                const hasPavedImages = storedPavedImages && storedPavedImages.tireImageUnreinforced && storedPavedImages.tireImageReinforced && storedPavedImages.tireImageGeogrid;

                logDebug(
                    'ButtonPanel',
                    'Paved GeoGrid Data Check:',
                    {
                        basicIsReady: !!geoGridData && !!geoGridProps.geoGridLayersImage,
                        storedPavedImages: !!storedPavedImages,
                        hasPavedImages: !!hasPavedImages,
                        finalIsGeoGridDataReady: isGeoGridDataReady,
                        note: 'GeoGrid PDF does not require paved images, only GeoGrid data and layer image',
                    },
                    LogTag.GEOGRID_CALC
                );
            } catch (error) {}
        }

        if (isGeoGridDataReady && projectInfo?.data) {
            // For airport projects, handle case where geoGridData might be undefined
            const finalGeoGridData = geoGridData || (projectTypeEnum === EProjectType.airport ? ({} as IGeogridData) : undefined);

            geoGridPdfProps = {
                geoGridData: finalGeoGridData, // Passa geoGridData come prop
                geogridImage: geoGridProps.geoGridLayersImage,
            };

            // Reason: Standalone geogrid projects with Railway geogridType should use RailwayGeoGridCore
            const geogridType = geoGridData?.type?.geogridType;
            const effectiveProjectType = geogridType === 'Railway' ? EProjectType.railways : projectTypeEnum;

            // Use project-specific GeoGrid PDF component
            switch (effectiveProjectType) {
                case EProjectType.paved:
                    geoGridDocumentToRender = <PavedGeoGridResultsPdfDocument dynamicProject={dynamicProject} projectInfo={projectInfo.data as IPdfProjectInfo} t={t} {...geoGridPdfProps} />;
                    break;
                case EProjectType.unpaved:
                    geoGridDocumentToRender = <UnpavedGeoGridResultsPdfDocument dynamicProject={dynamicProject} projectInfo={projectInfo.data as IPdfProjectInfo} t={t} {...geoGridPdfProps} />;
                    break;
                case EProjectType.railways:
                    geoGridDocumentToRender = <RailwayGeoGridResultsPdfDocument dynamicProject={dynamicProject} projectInfo={projectInfo.data as IPdfProjectInfo} t={t} {...geoGridPdfProps} />;
                    break;
                case EProjectType.airport:
                    geoGridDocumentToRender = <AirportGeoGridResultsPdfDocument dynamicProject={dynamicProject} projectInfo={projectInfo.data as IPdfProjectInfo} t={t} {...geoGridPdfProps} />;
                    break;
                case EProjectType.container_yard:
                case EProjectType.parking_deck:
                default:
                    // Default to container_yard component
                    geoGridDocumentToRender = <GeoGridResultsPdfDocument dynamicProject={dynamicProject} projectInfo={projectInfo.data as IPdfProjectInfo} t={t} {...geoGridPdfProps} />;
                    break;
            }
        }
    }

    // Separate logic for Combined PDF
    let isCombinedDataReady = false;
    let combinedDocumentToRender = <PlaceholderPdfDocument message={t('combined_data_not_available') || 'Combined data not available'} title={t('combined_pdf_placeholder_title') || 'Combined PDF'} />;

    if (showExportCombinedPdf) {
        const geoGridData = dynamicProject.data?.projectInfo?.data?.geoGrid as IGeogridData | undefined;

        // For railways, paved, container_yard, and parking_deck, combined PDF can be generated without project images
        // For other projects, we need the images to be ready
        const hasProjectData =
            projectTypeEnum === EProjectType.railways || projectTypeEnum === EProjectType.paved || projectTypeEnum === EProjectType.container_yard || projectTypeEnum === EProjectType.parking_deck
                ? !!projectInfo?.data && !!dynamicProject?.data
                : isSpecificDataReady;

        // For paved projects, check for stored paved images in addition to geoGridLayersImage
        let hasGeoGridData = !!geoGridData && !!geoGridProps.geoGridLayersImage;

        logDebug(
            'ButtonPanel',
            'Combined PDF - Initial State:',
            {
                projectTypeEnum,
                hasGeoGridData,
                hasProjectData,
                showExportCombinedPdf,
                geoGridData: !!geoGridData,
                geoGridLayersImage: !!geoGridProps.geoGridLayersImage,
            },
            LogTag.PDF_EXPORT
        );

        // For paved projects specifically, also check if paved images are stored in sessionStorage
        if (projectTypeEnum === EProjectType.paved && geoGridData && dynamicProject.data?.guid) {
            try {
                const storedPavedImages = pdfImageCaptureService.getStoredPavedImages(dynamicProject.data.guid);
                const hasPavedImages = storedPavedImages && storedPavedImages.tireImageUnreinforced && storedPavedImages.tireImageReinforced && storedPavedImages.tireImageGeogrid;

                logDebug(
                    'ButtonPanel',
                    'Combined PDF - Paved Check:',
                    {
                        storedPavedImages: !!storedPavedImages,
                        hasPavedImages: !!hasPavedImages,
                        originalHasGeoGridData: hasGeoGridData,
                    },
                    LogTag.PDF_EXPORT
                );

                // For paved projects, consider GeoGrid data ready if either condition is met
                hasGeoGridData = hasGeoGridData || !!hasPavedImages;
            } catch (error) {}
        }

        // For unpaved, paved, container_yard, parking_deck, and railways projects, we don't require GeoGrid data for combined PDF
        isCombinedDataReady =
            hasProjectData &&
            (projectTypeEnum === EProjectType.unpaved ||
            projectTypeEnum === EProjectType.paved ||
            projectTypeEnum === EProjectType.container_yard ||
            projectTypeEnum === EProjectType.parking_deck ||
            projectTypeEnum === EProjectType.railways
                ? true
                : hasGeoGridData);

        logDebug(
            'ButtonPanel',
            'Combined PDF - Final State:',
            {
                hasProjectData,
                hasGeoGridData,
                projectTypeEnum,
                isUnpaved: projectTypeEnum === EProjectType.unpaved,
                isCombinedDataReady,
            },
            LogTag.PDF_EXPORT
        );

        if (isCombinedDataReady && projectInfo?.data && dynamicProject.data && currentProjectImages) {
            // Enhanced logging for Task 2: Component Props Mapping Chain investigation

            if (useUnifiedProps && 'mappedImages' in effectiveProps && effectiveProps.mappedImages) {
            } else {
            }

            if (projectTypeEnum === EProjectType.paved) {
                // Get paved images directly from sessionStorage to include trafficData
                const guidToUse = projectGuid || dynamicProject.data?.guid || '';
                const storedPavedImages = pdfImageCaptureService.getStoredPavedImages(guidToUse);
                const pavedImages = storedPavedImages || imageExtractors.getPavedImages(currentProjectImages);

                const combinedPdfProps = {
                    output: resultsData,
                    tireImageUnreinforced: pavedImages?.tireImageUnreinforced || '',
                    tireImageReinforced: pavedImages?.tireImageReinforced || '',
                    tireImageGeogrid: pavedImages?.tireImageGeogrid || '',
                    chartImage: pavedImages?.chartImg || '',
                    geoGridData: geoGridData,
                    geogridImage: geoGridProps.geoGridLayersImage,
                    trafficData: pavedImages?.trafficData || undefined,
                    surfaceDesignImages: pavedImages?.surfaceDesignImages,
                    baseDesignImages: pavedImages?.baseDesignImages,
                    subbaseDesignImages: pavedImages?.subbaseDesignImages,
                };
                combinedDocumentToRender = <PavedCombinedResultsPdfDocument projectInfo={projectInfo.data as IPdfProjectInfo} dynamicProject={dynamicProject} t={t} {...combinedPdfProps} />;
            } else if (projectTypeEnum === EProjectType.unpaved) {
                // Get unpaved images from sessionStorage for correct mapping
                const unpavedImages = imageExtractors.getUnpavedImages(projectGuid || undefined);
                const combinedPdfProps = {
                    output: resultsData,
                    tireImageUnreinforced: unpavedImages?.tireImg1 || '',
                    tireImageReinforced: unpavedImages?.tireImg2 || '',
                    tireImageGeotextile: unpavedImages?.tireImg3 || '', // This will now have the correct geotextile image
                    chartImage: unpavedImages?.chartImg || '',
                    geoGridData: geoGridData,
                    geogridImage: geoGridProps.geoGridLayersImage,
                };
                combinedDocumentToRender = <UnpavedCombinedResultsPdfDocument projectInfo={projectInfo.data as IPdfProjectInfo} dynamicProject={dynamicProject} t={t} {...combinedPdfProps} />;
            } else if (projectTypeEnum === EProjectType.railways) {
                // Railway Combined PDF: uses 2 images (unreinforced + reinforced), no chart image for Railway projects
                const railwayImages = imageExtractors.getRailwayImages(currentProjectImages);

                // Ensure we have valid project data before rendering
                if (projectInfo?.data && dynamicProject?.data) {
                    const combinedPdfProps = {
                        output: resultsData,
                        tireImageUnreinforced: railwayImages?.tireImageUnreinforced || undefined,
                        tireImageReinforced: railwayImages?.tireImageReinforced || undefined,
                        // Railway projects don't have chart images - they display text-based results
                        geoGridData: geoGridData,
                        geogridImage: geoGridProps.geoGridLayersImage,
                    };
                    combinedDocumentToRender = <RailwayCombinedResultsPdfDocument projectInfo={projectInfo.data as any} dynamicProject={dynamicProject} t={t} {...combinedPdfProps} />;
                } else {
                }
            } else if (projectTypeEnum === EProjectType.airport) {
                // For airport combined PDF, get images from sessionStorage to include dynamic design sections
                const guidToUse = projectGuid || dynamicProject.data?.guid || '';
                const storedAirportImages = pdfImageCaptureService.getStoredAirportImages(guidToUse);
                const airportImages = storedAirportImages || imageExtractors.getAirportImages(currentProjectImages);
                isSpecificDataReady = pdfValidators.validateAirportImages(airportImages);

                if (isSpecificDataReady && airportImages && projectInfo?.data && dynamicProject.data) {
                    const combinedPdfProps = {
                        projectInfo: projectInfo.data,
                        dynamicProject: dynamicProject,
                        output: resultsData as IDynamicProjectOutputData | undefined,
                        images: airportImages,
                        geoGridData: geoGridData,
                        geogridImage: geoGridProps.geoGridLayersImage,
                        surfaceDesignImages: airportImages?.surfaceDesignImages,
                        baseDesignImages: airportImages?.baseDesignImages,
                        subbaseDesignImages: airportImages?.subbaseDesignImages,
                    };
                    combinedDocumentToRender = <AirportCombinedResultsPdfDocument {...combinedPdfProps} t={t} />;
                }
            } else {
                // Extract Container Yard / Parking Deck images using imageExtractors
                const containerYardImages = imageExtractors.getContainerYardImages(currentProjectImages);
                const combinedPdfProps = {
                    output: resultsData,
                    tireImageUnreinforced: containerYardImages?.tireImg1 || '',
                    tireImageReinforced: containerYardImages?.tireImg2 || '',
                    chartImage: containerYardImages?.chartImg || '',
                    geoGridData: geoGridData,
                    geogridImage: geoGridProps.geoGridLayersImage,
                };
                // Default to original combined PDF for container_yard and parking_deck
                combinedDocumentToRender = <CombinedResultsPdfDocument projectInfo={projectInfo.data as IPdfProjectInfo} dynamicProject={dynamicProject} t={t} {...combinedPdfProps} />;
            }
        }
    }

    const isDownloadable = canGenerateBasePdf && isSpecificDataReady && !currentPdfError;
    const isGeoGridDownloadable = canGenerateBasePdf && isGeoGridDataReady && !geoGridProps.geoGridPdfError;
    const isCombinedDownloadable = canGenerateBasePdf && isCombinedDataReady && !combinedPdfHelperProps.combinedPdfError;
    const pdfFileName = `${dynamicProject.data?.projectName || projectTypeEnum || 'results'}.pdf`;

    logDebug(
        'ButtonPanel',
        'PDF Button Debug:',
        {
            canGenerateBasePdf,
            isGeoGridDataReady,
            geoGridLayersImage: geoGridProps.geoGridLayersImage,
            geoGridPdfError: geoGridProps.geoGridPdfError,
        },
        LogTag.PDF_EXPORT
    );

    const commonButtonSx = {
        backgroundColor: 'transparent',
        color: '#004652',
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: 'rgba(0, 70, 82, 0.08)',
            color: '#004652',
        },
    };

    // Debug logging for GeoGrid PDF button visibility
    useEffect(() => {
        if (showExportGeoGridPdf) {
            logDebug(
                'ButtonPanel',
                'GeoGrid PDF Debug:',
                {
                    showExportGeoGridPdf,
                    projectTypeEnum,
                    canGenerateBasePdf,
                    isGeoGridDataReady,
                    isGeoGridDownloadable,
                    geoGridData: !!dynamicProject.data?.projectInfo?.data?.geoGrid,
                    geoGridLayersImage: !!geoGridProps.geoGridLayersImage,
                    geoGridPdfError: !!geoGridProps.geoGridPdfError,
                },
                LogTag.PDF_EXPORT
            );
        }
    }, [
        showExportGeoGridPdf,
        projectTypeEnum,
        canGenerateBasePdf,
        isGeoGridDataReady,
        isGeoGridDownloadable,
        dynamicProject.data?.projectInfo?.data?.geoGrid,
        geoGridProps.geoGridLayersImage,
        geoGridProps.geoGridPdfError,
    ]);

    // Debug logging for the main PDF button
    if (
        showExportPdf &&
        (projectTypeEnum === EProjectType.container_yard ||
            projectTypeEnum === EProjectType.parking_deck ||
            projectTypeEnum === EProjectType.unpaved ||
            projectTypeEnum === EProjectType.paved ||
            projectTypeEnum === EProjectType.airport)
    ) {
        const isUnpaved = projectTypeEnum === EProjectType.unpaved;
        const isPaved = projectTypeEnum === EProjectType.paved;
        const isAirport = projectTypeEnum === EProjectType.airport;
        let debugTitle = 'Container Yard PDF Button Debug:';

        if (isUnpaved) {
            debugTitle = 'Unpaved PDF Button Debug:';
        } else if (isPaved) {
            debugTitle = 'Paved PDF Button Debug:';
        } else if (isAirport) {
            debugTitle = 'Airport PDF Button Debug:';
        }

        const debugData: {
            projectInfoDataName: string | undefined;
            projectInfoDataExists: boolean;
            dynamicProjectDataExists: boolean;
            canGenerateBasePdf: boolean;
            isSpecificDataReady: boolean;
            // Allow additional debug properties
            [key: string]: unknown;
        } = {
            projectInfoDataName: dynamicProject.data?.projectName,
            projectInfoDataExists: !!projectInfo?.data,
            dynamicProjectDataExists: !!dynamicProject?.data,
            canGenerateBasePdf,
            isSpecificDataReady,
            currentPdfError,
            isDownloadable,
            currentIsGeneratingImages,
        };

        // Add project-specific debug info
        if (isPaved) {
            const pavedImages = imageExtractors.getPavedImages(currentProjectImages);
            debugData.pavedTireImageUnreinforcedExists = !!pavedImages?.tireImageUnreinforced;
            debugData.pavedTireImageReinforcedExists = !!pavedImages?.tireImageReinforced;
            debugData.pavedTireImageGeogridExists = !!pavedImages?.tireImageGeogrid;
            debugData.pavedChartImgExists = !!pavedImages?.chartImg;
            debugData.isGeneratingPavedPdfImagesProp = currentIsGeneratingImages;
            debugData.aashtoResultsExists = !!aashtoResults;
        } else if (projectTypeEnum === EProjectType.container_yard || projectTypeEnum === EProjectType.parking_deck) {
            const containerYardImages = imageExtractors.getContainerYardImages(currentProjectImages);
            debugData.containerYardTireImg1Exists = !!containerYardImages?.tireImg1;
            debugData.containerYardTireImg2Exists = !!containerYardImages?.tireImg2;
            debugData.containerYardChartImgExists = !!containerYardImages?.chartImg;
            debugData.isGeneratingContainerYardPdfImagesProp = currentIsGeneratingImages;
        } else if (projectTypeEnum === EProjectType.unpaved) {
            const unpavedImages = imageExtractors.getUnpavedImages();
            debugData.unpavedTireImg1Exists = !!unpavedImages?.tireImg1;
            debugData.unpavedTireImg2Exists = !!unpavedImages?.tireImg2;
            debugData.unpavedTireImg3Exists = !!unpavedImages?.tireImg3;
            debugData.unpavedChartImgExists = !!unpavedImages?.chartImg;
            debugData.isGeneratingUnpavedPdfImagesProp = currentIsGeneratingImages;
        } else if (projectTypeEnum === EProjectType.airport) {
            const airportImages = imageExtractors.getAirportImages(currentProjectImages);
            debugData.airportTireImageUnstabilizedExists = !!airportImages?.tireImageUnstabilized;
            debugData.airportTireImageIncreasedPassagesExists = !!airportImages?.tireImageIncreasedPassages;
            debugData.airportTireImageGeogridExists = !!airportImages?.tireImageGeogrid;
            debugData.airportChartImgExists = !!airportImages?.chartImg;
            debugData.isGeneratingAirportPdfImagesProp = currentIsGeneratingImages;
        } else if (projectTypeEnum === EProjectType.railways) {
            const railwayImages = imageExtractors.getRailwayImages(currentProjectImages);
            debugData.railwayTireImageUnreinforcedExists = !!railwayImages?.tireImageUnreinforced;
            debugData.railwayTireImageReinforcedExists = !!railwayImages?.tireImageReinforced;
            debugData.isGeneratingRailwayPdfImagesProp = currentIsGeneratingImages;
        }

        logDebug('ButtonPanel', debugTitle, debugData, LogTag.PDF_EXPORT);
    }

    return (
        <Grid container spacing={2} justifyContent="space-between" alignItems="center" sx={{ padding: 2 }}>
            {/* Bottoni SINISTRA */}
            <Grid item>
                <Stack direction="row" spacing={2}>
                    {showSave && !isReadOnly && (
                        <Button variant="contained" className="button-panel-btn" onClick={handleSave} disabled={saveDisabled} startIcon={<SaveIcon />} sx={commonButtonSx}>
                            {t('button_save')}
                        </Button>
                    )}
                    {showExportPdf &&
                        (projectTypeEnum === EProjectType.container_yard ||
                            projectTypeEnum === EProjectType.parking_deck ||
                            projectTypeEnum === EProjectType.unpaved ||
                            projectTypeEnum === EProjectType.paved ||
                            projectTypeEnum === EProjectType.airport ||
                            projectTypeEnum === EProjectType.railways) && ( // Mostra per tutti i tipi supportati incluso paved, airport e railways
                            <Tooltip
                                title={currentPdfError ? `${t('pdf_error')}: ${currentPdfError.message}` : !isDownloadable && !currentIsGeneratingImages ? t('pdf_tooltip_data_missing') : undefined}>
                                <span>
                                    {/* All project types now use StaggeredPDFDownloadLink for unified PDF generation */}
                                    <StaggeredPDFDownloadLink
                                        document={
                                            isDownloadable ? (
                                                documentToRender
                                            ) : (
                                                <PlaceholderPdfDocument message={t('data_not_available') || 'Data not available'} title={t('pdf_generation_error') || 'PDF Generation Error'} />
                                            )
                                        }
                                        fileName={pdfFileName}
                                        pdfKey={pdfKey}
                                        delay={0}
                                        style={{ textDecoration: 'none', pointerEvents: !isDownloadable || currentIsGeneratingImages ? 'none' : 'auto' }}>
                                        {({ loading, error }: { loading: boolean; error: Error | null }) => {
                                            if (error) {
                                                logError('ButtonPanel', 'Error rendering PDF link:', error, LogTag.PDF_EXPORT);
                                            }
                                            const showOverallLoading = loading || currentIsGeneratingImages;
                                            const buttonDisabledOverall = !isDownloadable || showOverallLoading;
                                            return (
                                                <Button
                                                    variant="contained"
                                                    className="button-panel-btn"
                                                    disabled={buttonDisabledOverall}
                                                    startIcon={showOverallLoading ? <CircularProgress size={20} sx={{ color: '#004652' }} /> : <PictureAsPdfIcon />}
                                                    sx={{
                                                        ...commonButtonSx,
                                                        backgroundColor: currentPdfError ? 'error.light' : commonButtonSx.backgroundColor,
                                                        color: currentPdfError ? 'error.contrastText' : commonButtonSx.color,
                                                        '&:hover': {
                                                            ...commonButtonSx['&:hover'],
                                                            backgroundColor: currentPdfError ? 'error.main' : commonButtonSx['&:hover'].backgroundColor,
                                                        },
                                                        cursor: buttonDisabledOverall ? 'not-allowed' : 'pointer',
                                                    }}>
                                                    {showOverallLoading ? t('button_generating_pdf') : currentPdfError ? t('button_pdf_error') : t('button_export_pdf')}
                                                </Button>
                                            );
                                        }}
                                    </StaggeredPDFDownloadLink>
                                </span>
                            </Tooltip>
                        )}

                    {showExportGeoGridPdf && (
                        <Tooltip
                            title={
                                geoGridProps.geoGridPdfError
                                    ? `${t('pdf_error')}: ${geoGridProps.geoGridPdfError.message}`
                                    : !isGeoGridDownloadable && !geoGridProps.isGeneratingGeoGridPdfImages
                                      ? t('pdf_tooltip_data_missing')
                                      : undefined
                            }>
                            <span>
                                <StaggeredPDFDownloadLink
                                    document={
                                        isGeoGridDownloadable ? (
                                            geoGridDocumentToRender
                                        ) : (
                                            <PlaceholderPdfDocument
                                                message={t('geogrid_data_not_ready') || 'GeoGrid data not ready for PDF generation'}
                                                title={t('geogrid_pdf_placeholder_title') || 'GeoGrid PDF'}
                                            />
                                        )
                                    }
                                    fileName={pdfFileName}
                                    pdfKey={pdfKey}
                                    delay={100}
                                    style={{ textDecoration: 'none', pointerEvents: !isGeoGridDownloadable || geoGridProps.isGeneratingGeoGridPdfImages ? 'none' : 'auto' }}>
                                    {({ loading, error }: { loading: boolean; error: Error | null }) => {
                                        if (error) {
                                            logError('ButtonPanel', 'Error rendering GeoGrid PDF link:', error, LogTag.PDF_EXPORT);
                                        }
                                        const showOverallLoading = loading || geoGridProps.isGeneratingGeoGridPdfImages;
                                        const buttonDisabledOverall = !isGeoGridDownloadable || showOverallLoading;

                                        return (
                                            <Button
                                                variant="contained"
                                                className="button-panel-btn"
                                                disabled={buttonDisabledOverall}
                                                startIcon={showOverallLoading ? <CircularProgress size={20} sx={{ color: '#004652' }} /> : <PictureAsPdfIcon />}
                                                sx={{
                                                    ...commonButtonSx,
                                                    backgroundColor: geoGridProps.geoGridPdfError ? 'error.light' : commonButtonSx.backgroundColor,
                                                    color: geoGridProps.geoGridPdfError ? 'error.contrastText' : commonButtonSx.color,
                                                    '&:hover': {
                                                        ...commonButtonSx['&:hover'],
                                                        backgroundColor: geoGridProps.geoGridPdfError ? 'error.main' : commonButtonSx['&:hover'].backgroundColor,
                                                    },
                                                    cursor: buttonDisabledOverall ? 'not-allowed' : 'pointer',
                                                }}>
                                                {showOverallLoading ? t('button_generating_pdf') : geoGridProps.geoGridPdfError ? t('button_pdf_error') : t('button_export_pdf')}
                                            </Button>
                                        );
                                    }}
                                </StaggeredPDFDownloadLink>
                            </span>
                        </Tooltip>
                    )}

                    {showExportCombinedPdf && (
                        <Tooltip
                            title={
                                combinedPdfHelperProps.combinedPdfError
                                    ? `${t('pdf_error')}: ${combinedPdfHelperProps.combinedPdfError.message}`
                                    : !isCombinedDownloadable && !combinedPdfHelperProps.isGeneratingCombinedPdfImages
                                      ? t('pdf_tooltip_data_missing') + ' ' + t('pdf_tooltip_visit_results_page')
                                      : undefined
                            }>
                            <span>
                                <StaggeredPDFDownloadLink
                                    document={
                                        isCombinedDownloadable ? (
                                            combinedDocumentToRender
                                        ) : (
                                            <PlaceholderPdfDocument
                                                message={t('combined_data_not_available') || 'Combined data not available'}
                                                title={t('combined_pdf_generation_error') || 'Combined PDF Generation Error'}
                                            />
                                        )
                                    }
                                    fileName={pdfFileName}
                                    pdfKey={pdfKey}
                                    delay={200}
                                    style={{ textDecoration: 'none', pointerEvents: !isCombinedDownloadable || combinedPdfHelperProps.isGeneratingCombinedPdfImages ? 'none' : 'auto' }}>
                                    {({ loading, error }: { loading: boolean; error: Error | null }) => {
                                        if (error) {
                                            logError('ButtonPanel', 'Error rendering Combined PDF link:', error, LogTag.PDF_EXPORT);
                                        }
                                        const showOverallLoading = loading || combinedPdfHelperProps.isGeneratingCombinedPdfImages;
                                        const buttonDisabledOverall = !isCombinedDownloadable || showOverallLoading;

                                        return (
                                            <Button
                                                variant="contained"
                                                className="button-panel-btn"
                                                disabled={buttonDisabledOverall}
                                                startIcon={showOverallLoading ? <CircularProgress size={20} sx={{ color: '#004652' }} /> : <PictureAsPdfIcon />}
                                                sx={{
                                                    ...commonButtonSx,
                                                    backgroundColor: combinedPdfHelperProps.combinedPdfError ? 'error.light' : commonButtonSx.backgroundColor,
                                                    color: combinedPdfHelperProps.combinedPdfError ? 'error.contrastText' : commonButtonSx.color,
                                                    '&:hover': {
                                                        ...commonButtonSx['&:hover'],
                                                        backgroundColor: combinedPdfHelperProps.combinedPdfError ? 'error.main' : commonButtonSx['&:hover'].backgroundColor,
                                                    },
                                                    cursor: buttonDisabledOverall ? 'not-allowed' : 'pointer',
                                                }}>
                                                {showOverallLoading ? t('button_generating_pdf') : combinedPdfHelperProps.combinedPdfError ? t('button_pdf_error') : t('button_export_combined_pdf')}
                                            </Button>
                                        );
                                    }}
                                </StaggeredPDFDownloadLink>
                            </span>
                        </Tooltip>
                    )}
                </Stack>
            </Grid>
            {/* Bottoni DESTRA */}
            <Grid item>
                <Stack direction="row" spacing={2}>
                    {showGeoGrid && (
                        <Button variant="contained" className="button-panel-btn geogrid-label" onClick={handleShowGeoGrid} disabled={showGeoGridDisabled} startIcon={<GridOnIcon />} sx={commonButtonSx}>
                            {t('button_show_geogrid')}
                        </Button>
                    )}
                    {showExecute && (
                        <Button variant="contained" className="button-panel-btn" onClick={handleExecute} disabled={executeDisabled} startIcon={<PlayArrowIcon />} sx={commonButtonSx}>
                            {t('button_execute')}
                        </Button>
                    )}
                    {showExecuteGeoGrid && (
                        <Button variant="contained" className="button-panel-btn" onClick={handleExecuteGeoGrid} disabled={executeGeoGridDisabled} startIcon={<PlayArrowIcon />} sx={commonButtonSx}>
                            {t('button_execute_geogrid')}
                        </Button>
                    )}
                </Stack>
            </Grid>
        </Grid>
    );
};

export default ButtonPanel;
